import { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import LoginPage from './pages/login/LoginPage';
import ChaptersPage from './pages/ChaptersPage';
import HomePage from './pages/HomePage';
import ChapterDetailsPage from './pages/ChapterDetailsPage';
import AdminSentences from './pages/admin/AdminSentences';
import AdminHome from './pages/admin/AdminHome';
import AdminActivityEditor from './pages/admin/AdminActivityEditor';
import AdminUserManagement from './pages/admin/AdminUserManagement';
import ChapterViewer from './pages/ChapterViewer';
import StudentCreation from './pages/profile/StudentCreation';
import ChapterConfigPage from './pages/ChapterConfigPage';
import PracticePage from './pages/PracticePage';
import LongQuestionSelectionPage from './pages/LongQuestionSelectionPage';
import LevelLoader from './pages/activity/LevelLoader';
import Header from './components/Header';
import HelpModal from './components/HelpModal';
import FeedbackModal from './components/FeedbackModal';
import ProfileModal from './components/ProfileModal';
import './App.css';
import Constants from './utilities/constants/Constants';
import { signInWithEmailAndPassword, signInWithPopup, sendPasswordResetEmail, onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, provider } from './firebase';
import { getStudentsByUserEmail, getChapterConfigsBySchoolAndUser, getUserByEmail, updateStudentSyllabus, updateStudentSettings } from '@dataconnect/generated';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FaGraduationCap, FaSchool } from 'react-icons/fa';
import Clarity from '@microsoft/clarity';
import { getStudentSettings } from './utilities/settingsHelper';

export default function App() {
  const [currentPage, setCurrentPage] = useState('login');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [students, setStudents] = useState([]);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckLoading, setAdminCheckLoading] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [completedItems, setCompletedItems] = useState({}); // { activityId: [itemIndexes] }
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [customBackHandler, setCustomBackHandler] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [activitySourcePage, setActivitySourcePage] = useState(null); // Track where activity was started from
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [schoolData, setSchoolData] = useState(null);
  const [homeViewMode, setHomeViewMode] = useState('learn');
  const [audioEnabled, setAudioEnabled] = useState(true);

  const projectId = "uicfr8u2ei";
  Clarity.init(projectId);

  // Initialize Firebase Functions
  const functions = getFunctions();
  const checkAdminStatus = httpsCallable(functions, 'checkAdminStatus');

  // Check if user is admin using server-side function
  const checkUserAdminStatus = async () => {
    setAdminCheckLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setIsAdmin(false);
        return false;
      }

      try {
        // Try server-side admin check first
        const result = await checkAdminStatus();
        const serverIsAdmin = result.data?.isAdmin || false;

        // If server says user is admin, trust it
        if (serverIsAdmin) {
          setIsAdmin(true);
          return true;
        }

        // If server says user is not admin, fall back to client-side check
        const adminEmails = [
          'admin@zenokids.com',
          'saritha@zenokids.com',
          'sarithakamath@gmail.com'
        ];

        const clientIsAdmin = adminEmails.includes(currentUser.email);

        setIsAdmin(clientIsAdmin);
        return clientIsAdmin;

      } catch (serverError) {
        // Fallback to client-side check if server call fails
        const adminEmails = [
          'admin@zenokids.com',
          'saritha@zenokids.com',
          'sarithakamath@gmail.com'
        ];

        const userIsAdmin = adminEmails.includes(currentUser.email);

        setIsAdmin(userIsAdmin);
        return userIsAdmin;
      }
    } catch (error) {
      // Ignore errors
      setIsAdmin(false);
      return false;
    } finally {
      setAdminCheckLoading(false);
    }
  };

  // Update audio state when student changes
  useEffect(() => {
    if (selectedStudent) {
      const settings = getStudentSettings(selectedStudent);
      setAudioEnabled(settings.audioEnabled !== false);
    }
  }, [selectedStudent]);

  const handleAudioToggle = async () => {
    if (!selectedStudent) return;

    const newAudioState = !audioEnabled;
    setAudioEnabled(newAudioState);

    try {
      // Get current settings
      let allSettings = {};
      if (selectedStudent.settings) {
        allSettings = JSON.parse(selectedStudent.settings);
      }

      // Determine language
      const language = selectedStudent.subjects && selectedStudent.subjects.length > 0
        ? selectedStudent.subjects[0].toLowerCase()
        : 'kannada';

      // Update audioEnabled for this language
      if (!allSettings[language]) {
        allSettings[language] = {};
      }
      allSettings[language].audioEnabled = newAudioState;

      const settingsJson = JSON.stringify(allSettings);

      // Save to database
      await updateStudentSettings({
        id: selectedStudent.id,
        settings: settingsJson
      });

      // Update local student state
      setSelectedStudent(prev => ({
        ...prev,
        settings: settingsJson
      }));
    } catch (error) {
      console.error('Failed to update audio setting:', error);
      // Revert local state on error
      setAudioEnabled(!newAudioState);
    }
  };

  const handleLogin = async (email, password) => {
    await signInWithEmailAndPassword(auth, email, password);
  }

  const handleSignWithGoogle = async () => {
    try {
      await signInWithPopup(auth, provider);
      // The onAuthStateChanged listener will handle the rest for regular users
    } catch (error) {


      // Handle specific error cases
      if (error.code === 'auth/popup-blocked') {
        alert('Popup was blocked by browser. Please allow popups for this site and try again.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        // User closed the popup
      } else if (error.code === 'auth/unauthorized-domain') {

        alert('This domain is not authorized for Google sign-in. Please contact support.');
      } else {

        alert('Google sign-in failed. Please try again.');
      }
    }
  }

  const handleResetPassword = async (email) => {
    // No longer needed for Google-only auth
  }

  const handleSignup = () => {
    // No longer needed for Google-only auth
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      // The onAuthStateChanged listener will handle clearing state
      setStudents([]);
      setSelectedStudent(null);
      setIsLoadingProfile(false);
    } catch (error) {

      // Ignore errors
    }
  };

  const handleEditContent = (chapter) => {
    setSelectedChapter(chapter);
    setCurrentPage('adminSentences');
  };

  const handleViewContent = (chapter) => {
    setSelectedChapter(chapter);
    setCurrentPage('viewChapter');
  };

  const handleEditActivities = (chapter) => {
    setSelectedChapter(chapter);
    setCurrentPage('adminActivityEditor');
  };

  const handleViewActivities = (chapter) => {
    setSelectedChapter(chapter);
    setCurrentPage('chapterDetails');
  };


  const handleUserManagement = () => {
    setCurrentPage('adminUserManagement');
  };

  // Effect to fetch students when user is available
  useEffect(() => {
    const checkAdminAndFetchStudents = async () => {
      // Check if user is admin first
      const userIsAdmin = await checkUserAdminStatus();
      if (userIsAdmin) {
        setCurrentPage('adminHome');
        return;
      }

      // If not admin, fetch students
      if (user?.uid) {
        try {
          setIsLoadingProfile(true);
          const result = await getStudentsByUserEmail({ email: user.email });

          if (result.data?.students && result.data.students.length > 0) {
            setStudents(result.data.students);

            // Navigate to home with the first student (one student per email policy)
            const updatedStudent = await autoSelectDefaultConfig(result.data.students[0]);
            setSelectedStudent(updatedStudent);
            flushSync(() => {
              setIsLoadingProfile(false);
            });
            setCurrentPage('home');
          } else {
            // If no students found, go to add students page
            flushSync(() => {
              setIsLoadingProfile(false);
            });
            setCurrentPage('studentCreation');
          }
        } catch (error) {

          // If there's an error, go to chapters anyway
          flushSync(() => {
            setIsLoadingProfile(false);
          });
          setCurrentPage('chapters');
        }
      }
    };

    checkAdminAndFetchStudents();
  }, [user]);

  // Authentication state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        setIsLoggedIn(true);
        setIsAuthReady(true);
      } else {
        setUser(null);
        setIsLoggedIn(false);
        setIsAuthReady(true);
        setCurrentPage('login');
      }
    });

    return () => unsubscribe();
  }, []);

  // Helper function to auto-select default config for a student
  const autoSelectDefaultConfig = async (student) => {
    // If student already has a config selected, don't change it
    if (student.selectedConfigId) {
      return student;
    }

    try {
      // Get user's internal ID
      const userResult = await getUserByEmail({ email: user.email });
      if (!userResult.data?.users?.length) {
        return student;
      }

      const internalUserId = userResult.data.users[0].id;

      // Fetch available configs
      const params = {
        userId: internalUserId,
        grade: student.gradeLevel,
        ...(student.school?.id && { schoolId: student.school.id })
      };

      const configResult = await getChapterConfigsBySchoolAndUser(params);
      if (!configResult.data) {
        return student;
      }

      const schoolConfigs = configResult.data.schoolConfigs || [];
      const userConfigs = configResult.data.userConfigs || [];

      let selectedConfigId = null;

      // Priority 1: School default config
      const schoolDefault = schoolConfigs.find(c => c.isDefault);
      if (schoolDefault) {
        selectedConfigId = schoolDefault.id;
      }
      // Priority 2: Personal config (any)
      else if (userConfigs.length > 0) {
        selectedConfigId = userConfigs[0].id;
      }
      // Priority 3: Any default config
      else {
        const anyDefault = schoolConfigs.find(c => c.isDefault);
        if (anyDefault) {
          selectedConfigId = anyDefault.id;
        }
        // Priority 4: Any school config
        else if (schoolConfigs.length > 0) {
          selectedConfigId = schoolConfigs[0].id;
        }
      }

      // If we found a config to auto-select, update the student
      if (selectedConfigId) {
        const updateResult = await updateStudentSyllabus({
          id: student.id,
          selectedConfigId: selectedConfigId
        });

        if (updateResult.data?.student_update) {
          return { ...student, selectedConfigId };
        }
      }
    } catch (error) {
      console.error('Failed to auto-select default config:', error);
    }

    return student;
  };

  const handleSelectStudent = async (student) => {
    const updatedStudent = await autoSelectDefaultConfig(student);
    setSelectedStudent(updatedStudent);
    setCurrentPage('home');
  };

  // Refresh student data to get updated selectedConfigId
  const refreshStudentData = async () => {
    if (!user?.email || !selectedStudent?.id) return;

    try {
      const result = await getStudentsByUserEmail({ email: user.email });
      if (result.data?.students) {
        const updatedStudent = result.data.students.find(s => s.id === selectedStudent.id);
        if (updatedStudent) {
          setSelectedStudent(updatedStudent);
        }
      }
    } catch (error) {
      console.error('Failed to refresh student data:', error);
    }
  };

  const handleProfileUpdate = async (updates) => {
    if (updates) {
      // Create a clean updated student object
      setSelectedStudent(prev => {
        const newStudent = { ...prev, ...updates };
        
        // Ensure gradeLevel is always a number
        if (newStudent.gradeLevel) {
          newStudent.gradeLevel = parseInt(newStudent.gradeLevel);
        }

        // If school object was updated, also sync the schoolData state
        if (updates.school) {
          setSchoolData(updates.school);
        }
        
        return newStudent;
      });
    }
  };

  const handleStudentsAdded = (createdStudents) => {
    setStudents(createdStudents);

    // One student per email policy: select the first student and go to home
    if (createdStudents.length > 0) {
      setSelectedStudent(createdStudents[0]);
      setCurrentPage('home');
    }
  };

  const handleViewChapter = (chapter) => {
    setSelectedChapter(chapter);
    setCurrentPage('viewChapter');
  };

  const handleStartActivity = (activity, sourcePage = 'chapterDetails') => {
    setSelectedActivity(activity);
    setActivitySourcePage(sourcePage); // Track where the activity was started from
    
    // Special handling for Long Question Answers - go to question selection first in learn chapterwise
    if (activity.activityType === 'LONG_QUESTION_ANSWERS' && sourcePage !== 'practice') {
      setCurrentPage('longQuestionSelection');
      return;
    }

    // Route to appropriate activity page based on activity type
    switch (activity.activityType) {
      case 'WORD_MEANING':
        setCurrentPage('wordMeaningActivity');
        break;
      case 'DUAL_MEANING':
        setCurrentPage('dualMeaningActivity');
        break;
      case 'MATCH':
        setCurrentPage('matchActivity');
        break;
      case 'OPPOSITES':
        setCurrentPage('oppositesActivity');
        break;
      case 'WHO_SAID_TO_WHOM':
        setCurrentPage('whoSaidToWhomActivity');
        break;
      case 'QUESTION_ANSWERS':
        setCurrentPage('questionAnswerActivity');
        break;
      case 'LONG_QUESTION_ANSWERS':
        setCurrentPage('longQuestionAnswerActivity');
        break;
      case 'WORD_PRACTICE':
        setCurrentPage('wordPracticeActivity');
        break;
      case 'WORD_SPLIT':
        setCurrentPage('wordSplitActivity');
        break;
      case 'CREATIVE_WRITING':
        setCurrentPage('creativeWritingActivity');
        break;
      case 'FILL_IN_BLANK_MCQ':
        setCurrentPage('fillInBlankMcqActivity');
        break;
      case 'NUMBERS':
        setCurrentPage('numbersActivity');
        break;
      case 'GENDERS':
        setCurrentPage('gendersActivity');
        break;
      case 'CHANGE_GENDERS':
        setCurrentPage('changeGendersActivity');
        break;
      case 'TENSES':
        setCurrentPage('tensesActivity');
        break;
      case 'SINGULAR_PLURAL':
        setCurrentPage('singularPluralActivity');
        break;
      case 'MAKE_SENTENCES':
        setCurrentPage('makeSentencesActivity');
        break;
      case 'JOIN_WORDS':
        setCurrentPage('joinWordsActivity');
        break;
      case 'CUSTOM':
        setCurrentPage('customActivity');
        break;
      default:

        break;
    }
  };


  const handleActivityComplete = useCallback(() => {
    // Track completion for long question items
    if (selectedActivity?.activityType === 'LONG_QUESTION_ANSWERS' && selectedItem) {
      const match = selectedItem.id.match(/-q(\d+)$/);
      if (match) {
        const itemIndex = parseInt(match[1]);
        setCompletedItems(prev => {
          const activityCompletions = prev[selectedActivity.id] || [];
          if (!activityCompletions.includes(itemIndex)) {
            return {
              ...prev,
              [selectedActivity.id]: [...activityCompletions, itemIndex]
            };
          }
          return prev;
        });
      }
      
      // After completing a single question, go back to selection page
      setCurrentPage('longQuestionSelection');
      setSelectedItem(null);
      return;
    }

    // Default completion logic
    const returnPage = activitySourcePage || 'chapterDetails';
    setCurrentPage(returnPage);
    setActivitySourcePage(null); // Clear source page
  }, [activitySourcePage]);

  const handleBackFromActivity = useCallback(() => {
    setSelectedActivity(null);
    setSelectedItem(null);
    setCustomBackHandler(null); // Clear custom back handler
    
    let returnPage = activitySourcePage || 'chapterDetails';
    
    // If coming from practice, we want to go back to HomePage in practice mode
    if (returnPage === 'practice') {
      setHomeViewMode('practice');
      returnPage = 'home';
    }
    
    setCurrentPage(returnPage);
    setActivitySourcePage(null); // Clear source page
  }, [activitySourcePage]);

  const handleBackHandlerChange = useCallback((handler) => {
    setCustomBackHandler(() => handler);
  }, []);

  // Get header configuration based on current page
  const getHeaderConfig = () => {
    const pagesWithoutHeader = ['login'];

    if (pagesWithoutHeader.includes(currentPage)) {
      return { show: false };
    }

    // Profile and Help buttons should only be visible to non-admin users
    const showProfileButton = !isAdmin;
    const showHelpButton = !isAdmin;
    const showFeedbackButton = !isAdmin;

    const config = {
      show: true,
      showBackButton: false,
      onBack: null,
      title: 'ZenoKids',
      subtitle: null,
      showHelpButton: showHelpButton,
      showFeedbackButton: showFeedbackButton,
      showProfileButton: showProfileButton,
      showLogoutButton: true
    };

    // Configure based on current page
    switch (currentPage) {
      case 'home':
        config.title = 'ZenoKids';
        config.subtitle = selectedStudent && (
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span>Welcome back, {selectedStudent.name}!</span>
            <span>•</span>
            <div className="flex items-center space-x-1">
              <FaGraduationCap className="text-xs" />
              <span>Grade {selectedStudent.gradeLevel}</span>
            </div>
            {schoolData && (
              <>
                <span>•</span>
                <div className="flex items-center space-x-1">
                  <FaSchool className="text-xs" />
                  <span>{schoolData.name}</span>
                </div>
              </>
            )}
          </div>
        );
        break;

      case 'chapters':
        config.title = 'ZenoKids';
        config.showBackButton = true;
        config.onBack = () => setCurrentPage(user?.email?.startsWith('admin') ? 'adminHome' : 'home');
        config.subtitle = selectedStudent && (
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span>Hi {selectedStudent.name}!</span>
            <span>•</span>
            <div className="flex items-center space-x-1">
              <FaGraduationCap className="text-xs" />
              <span>Grade {selectedStudent.gradeLevel}</span>
            </div>
            {schoolData && (
              <>
                <span>•</span>
                <div className="flex items-center space-x-1">
                  <FaSchool className="text-xs" />
                  <span>{schoolData.name}</span>
                </div>
              </>
            )}
          </div>
        );
        break;

      case 'chapterDetails':
        config.title = selectedChapter?.title || 'Chapter Activities';
        config.subtitle = selectedChapter?.description;
        config.showBackButton = true;
        config.onBack = () => setCurrentPage(user?.email?.startsWith('admin') ? 'adminHome' : 'chapters');
        break;

      case 'viewChapter':
        config.title = selectedChapter?.title || 'Read Chapter';
        config.showBackButton = true;
        config.onBack = () => setCurrentPage(user?.email?.startsWith('admin') ? 'adminHome' : 'chapterDetails');
        break;

      case 'longQuestionSelection':
        config.title = 'Select Long Question';
        config.showBackButton = true;
        config.onBack = () => setCurrentPage('chapterDetails');
        break;

      case 'wordMeaningActivity':
      case 'dualMeaningActivity':
      case 'matchActivity':
      case 'oppositesActivity':
      case 'whoSaidToWhomActivity':
      case 'questionAnswerActivity':
      case 'longQuestionAnswerActivity':
      case 'wordPracticeActivity':
      case 'wordSplitActivity':
      case 'creativeWritingActivity':
      case 'fillInBlankMcqActivity':
      case 'numbersActivity':
      case 'gendersActivity':
      case 'tensesActivity':
      case 'singularPluralActivity':
      case 'joinWordsActivity':
      case 'fillInBlanksParagraphActivity':
      case 'customActivity': {
        // Derive title from activity type using Constants
        const activityTypeLabel = selectedActivity?.activityType
          ? Constants.ACTIVITY_TYPE_LABELS[selectedActivity.activityType]
          : selectedActivity?.title || 'Activity';
        const chapterName = selectedChapter?.title || '';
        
        if (currentPage === 'longQuestionAnswerActivity' && selectedItem) {
          try {
            const content = JSON.parse(selectedItem.baseContent);
            const questionText = content.questions?.[0]?.question?.languageText || content.questions?.[0]?.question?.englishText || '';
            config.title = questionText ? `${activityTypeLabel}: ${questionText}` : activityTypeLabel;
          } catch (e) {
            config.title = `${chapterName} - ${activityTypeLabel}`;
          }
        } else {
          config.title = chapterName ? `${chapterName} - ${activityTypeLabel}` : activityTypeLabel;
        }

        config.showBackButton = true;
        // Use custom back handler from LevelLoader if available, otherwise use default
        config.onBack = customBackHandler || handleBackFromActivity;
        break;
      }

      case 'studentCreation':
        config.title = 'Student Profile';
        break;

      case 'chapterConfig':
        config.title = 'Chapter Configurations';
        config.showBackButton = true;
        config.onBack = () => setCurrentPage('home');
        break;

      case 'adminHome':
      case 'adminSentences':
      case 'adminActivityEditor':
      case 'adminUserManagement':
        config.title = 'Admin Panel';
        config.showBackButton = currentPage !== 'adminHome';
        config.onBack = () => setCurrentPage('adminHome');
        break;

      default:
        break;
    }

    return config;
  };

  // Loading component
  const LoadingScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading your profile...</p>
      </div>
    </div>
  );

  // Admin route protection helper
  const renderAdminPage = (component) => {
    if (!isAdmin) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <div className="bg-red-50 border border-red-200 text-red-800 p-6 rounded-lg max-w-md mx-auto" role="alert">
              <h4 className="text-xl font-semibold mb-3">🚫 Access Denied</h4>
              <p className="mb-4">You don&apos;t have admin privileges to access this page.</p>
              <hr className="border-red-200 my-4" />
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                onClick={() => setCurrentPage(isLoggedIn ? 'chapters' : 'login')}
              >
                {isLoggedIn ? 'Go to Chapters' : 'Go to Login'}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return component;
  };

  // A switch statement to render the correct page
  const renderPage = () => {
    // Show loading screen while authentication is being checked
    if (!isAuthReady || adminCheckLoading) {
      return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      );
    }

    // Show loading screen while fetching user data
    if (isLoggedIn && !user?.email?.startsWith('admin') && isLoadingProfile) {
      return <LoadingScreen />;
    }

    switch (currentPage) {
      case 'adminHome':
        return renderAdminPage(
          <AdminHome
            onLogout={handleLogout}
            onViewContent={(chapter) => handleViewContent(chapter)}
            onEditContent={(chapter) => handleEditContent(chapter)}
            onEditActivities={(chapter) => handleEditActivities(chapter)}
            onViewActivities={(chapter) => handleViewActivities(chapter)}
            onUserManagement={handleUserManagement}
          />
        );
      case 'adminSentences':
        return renderAdminPage(<AdminSentences selectedChapter={selectedChapter} onBack={() => setCurrentPage('adminHome')} onLogout={handleLogout} />);
      case 'adminActivityEditor':
        return renderAdminPage(<AdminActivityEditor selectedChapter={selectedChapter} onBack={() => setCurrentPage('adminHome')} />);
      case 'adminUserManagement':
        return renderAdminPage(<AdminUserManagement onBack={() => setCurrentPage('adminHome')} />);
      case 'viewChapter':
        return <ChapterViewer selectedChapter={selectedChapter} student={selectedStudent} onBack={() => setCurrentPage(user?.email?.startsWith('admin') ? 'adminHome' : 'chapterDetails')} onLogout={handleLogout} />;
      case 'chapters':
        return (
          <ChaptersPage 
            key={`chapters-${selectedStudent?.id}-${selectedStudent?.gradeLevel}-${selectedStudent?.name}`}
            selectedStudent={selectedStudent} 
            onLogout={handleLogout} 
            onSelectChapter={(chapter) => { setSelectedChapter(chapter); setCurrentPage('chapterDetails'); }} 
            onSchoolDataLoad={setSchoolData} 
          />
        );
      case 'home':
        return (
          <HomePage 
            key={`home-${selectedStudent?.id}-${selectedStudent?.gradeLevel}-${selectedStudent?.name}`}
            selectedStudent={selectedStudent} 
            user={user}
            onLogout={handleLogout} 
            onViewChapters={() => setCurrentPage('chapters')} 
            onSchoolDataLoad={setSchoolData} 
            onSelectSyllabus={() => setCurrentPage('chapterConfig')} 
            onPracticeTest={() => { setHomeViewMode('practice'); setCurrentPage('home'); }}
            onSelectChapter={(chapter) => { setSelectedChapter(chapter); setCurrentPage('chapterDetails'); }}
            onStartActivity={handleStartActivity}
            initialViewMode={homeViewMode}
          />
        );
      case 'chapterDetails':
        return (
          <ChapterDetailsPage 
            selectedChapter={selectedChapter} 
            selectedStudent={selectedStudent} 
            onLogout={handleLogout} 
            onBack={() => setCurrentPage(user?.email?.startsWith('admin') ? 'adminHome' : 'home')} 
            onViewChapter={handleViewChapter} 
            onStartActivity={handleStartActivity} 
            user={user}
            isAdmin={isAdmin}
          />
        );
      case 'longQuestionSelection':
        return (
          <LongQuestionSelectionPage
            activity={selectedActivity}
            completedIndexes={completedItems[selectedActivity?.id] || []}
            onBack={() => setCurrentPage('chapterDetails')}
            onSelectQuestion={(question, index) => {
              const miniActivity = {
                ...selectedActivity,
                id: `${selectedActivity.id}-q${index}`,
                baseContent: JSON.stringify({ questions: [question] })
              };
              setSelectedItem(miniActivity);
              setCurrentPage('longQuestionAnswerActivity');
            }}
          />
        );
      case 'wordMeaningActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.WORD_MEANING} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'dualMeaningActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.DUAL_MEANING} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'matchActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.MATCH} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'oppositesActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.OPPOSITES} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'whoSaidToWhomActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.WHO_SAID_TO_WHOM} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'questionAnswerActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.QUESTION_ANSWERS} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'longQuestionAnswerActivity':
        return <LevelLoader activity={selectedItem || selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={selectedItem ? () => setCurrentPage('longQuestionSelection') : handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.LONG_QUESTION_ANSWERS} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'wordPracticeActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.WORD_PRACTICE} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'wordSplitActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.WORD_SPLIT} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'creativeWritingActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.CREATIVE_WRITING} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'fillInBlankMcqActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.FILL_IN_BLANK_MCQ} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'numbersActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.NUMBERS} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'gendersActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.GENDERS} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'changeGendersActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.CHANGE_GENDERS} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'tensesActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.TENSES} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'singularPluralActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.SINGULAR_PLURAL} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'makeSentencesActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.MAKE_SENTENCES} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'joinWordsActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.JOIN_WORDS} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'customActivity':
        return <LevelLoader activity={selectedActivity} selectedChapter={selectedChapter} student={selectedStudent} onBack={handleBackFromActivity} onComplete={handleActivityComplete} activityType={Constants.ACTIVITY_TYPES.CUSTOM} onBackHandlerChange={handleBackHandlerChange} activitySourcePage={activitySourcePage} />;
      case 'chapterConfig':
        return <ChapterConfigPage selectedStudent={selectedStudent} onBack={() => { refreshStudentData(); setHomeViewMode('practice'); setCurrentPage('home'); }} user={user} isAdmin={isAdmin} />;
      case 'studentCreation':
        return <StudentCreation user={user} onStudentsAdded={handleStudentsAdded} onLogout={handleLogout} />;
      default:
        return <LoginPage onSignWithGoogle={handleSignWithGoogle} />;
    }
  };

  const headerConfig = getHeaderConfig();

  return (
    <>
      <div className="font-sans antialiased text-dark min-h-screen flex flex-col">
        {/* Header - shown on all pages except login/signup */}
        {headerConfig.show && (
          <Header
            title={headerConfig.title}
            subtitle={headerConfig.subtitle}
            showBackButton={headerConfig.showBackButton}
            onBack={headerConfig.onBack}
            showHelpButton={headerConfig.showHelpButton}
            onHelp={() => setShowHelpModal(true)}
            showFeedbackButton={headerConfig.showFeedbackButton}
            onFeedback={() => setShowFeedbackModal(true)}
            showProfileButton={headerConfig.showProfileButton}
            onProfile={() => setShowProfile(true)}
            showLogoutButton={headerConfig.showLogoutButton}
            onLogout={handleLogout}
            audioEnabled={audioEnabled}
            onAudioToggle={isLoggedIn && !user?.email?.startsWith('admin') ? handleAudioToggle : null}
          />
        )}

        {/* Main Content */}
        <div className="flex-1">
          {renderPage()}
        </div>

        {/* Help Modal */}
        {showHelpModal && (
          <HelpModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} />
        )}

        {/* Feedback Modal */}
        {showFeedbackModal && (
          <FeedbackModal
            isOpen={showFeedbackModal}
            onClose={() => setShowFeedbackModal(false)}
          />
        )}

        {/* Profile Modal */}
        {showProfile && (
          <ProfileModal
            isOpen={showProfile}
            onClose={() => setShowProfile(false)}
            student={selectedStudent}
            onProfileUpdate={handleProfileUpdate}
          />
        )}
      </div>
    </>
  );
}
